/**
 * P1 member-portal region helpers.
 * Prefer hostname for deployed sites; fall back to REACT_APP_P1_REGION for localhost/builds.
 */

export const AU_PORTAL_URL =
  process.env.REACT_APP_AU_PORTAL_URL || "https://my.priorityonewashclub.com";

export const NZ_PORTAL_URL =
  process.env.REACT_APP_NZ_PORTAL_URL || "https://my.priorityonewashclub.co.nz";

/**
 * @returns {"AU" | "NZ"}
 */
export function getCurrentP1Region() {
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();

    // NZ prod (and any *.co.nz host)
    if (host.includes("priorityonewashclub.co.nz") || host.endsWith(".co.nz")) {
      return "NZ";
    }

    // AU prod / Amplify staging
    if (
      host.includes("priorityonewashclub.com") ||
      host.includes("dm6wsofv5048b.amplifyapp.com") ||
      host.includes("p1-staging")
    ) {
      return "AU";
    }
  }

  return process.env.REACT_APP_P1_REGION === "NZ" ? "NZ" : "AU";
}

export function getPortalUrlForRegion(region) {
  return String(region).toUpperCase() === "NZ" ? NZ_PORTAL_URL : AU_PORTAL_URL;
}

export function getOtherP1Region(region = getCurrentP1Region()) {
  return String(region).toUpperCase() === "NZ" ? "AU" : "NZ";
}

const CENTER_OF_SYDNEY = { lat: -33.8688, lng: 151.2093 };
const CENTER_OF_AUCKLAND = { lat: -36.8485, lng: 174.7633 };

/**
 * Region for maps: `?region=AU|NZ` query param, then hostname / env.
 * @returns {"AU" | "NZ"}
 */
export function getMapRegion() {
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("region");
    if (q && /^(AU|NZ)$/i.test(q)) return q.toUpperCase();
  }
  return getCurrentP1Region();
}

export function getMapDefaultCenter(region = getMapRegion()) {
  return region === "NZ" ? CENTER_OF_AUCKLAND : CENTER_OF_SYDNEY;
}

export function getMapGeocodeCountry(region = getMapRegion()) {
  return region === "NZ" ? "NZ" : "AU";
}

export function getMapGeocodeCountryName(region = getMapRegion()) {
  return region === "NZ" ? "New Zealand" : "Australia";
}

export const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"];

export const NZ_REGIONS = [
  "Auckland",
  "Bay of Plenty",
  "Canterbury",
  "Gisborne",
  "Hawke's Bay",
  "Manawatū-Whanganui",
  "Marlborough",
  "Nelson",
  "Northland",
  "Otago",
  "Southland",
  "Taranaki",
  "Tasman",
  "Waikato",
  "Wellington",
  "West Coast"
];

export function isNzP1Region(region = getCurrentP1Region()) {
  return String(region).toUpperCase() === "NZ";
}

/** AU state codes or NZ regions for vehicle / address selects. */
export function getVehicleStateOptions(region = getCurrentP1Region()) {
  return isNzP1Region(region) ? NZ_REGIONS : AU_STATES;
}

export function getVehicleStateFieldLabel(region = getCurrentP1Region()) {
  return isNzP1Region(region) ? "Region" : "State";
}

export function isAuVehicleState(state) {
  return AU_STATES.includes(String(state || "").trim().toUpperCase());
}

/** Keep AU codes uppercase; keep NZ region names in their canonical form. */
export function canonicalizeVehicleState(value, region = getCurrentP1Region()) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const options = getVehicleStateOptions(region);
  const match = options.find((option) => option.toLowerCase() === trimmed.toLowerCase());
  if (match) return match;
  return isNzP1Region(region) ? trimmed : trimmed.toUpperCase();
}

export function getVehicleStateOptionsForSelect(currentValue, region = getCurrentP1Region()) {
  const options = [...getVehicleStateOptions(region)];
  const canonical = canonicalizeVehicleState(currentValue, region);
  if (canonical && !options.some((option) => option.toLowerCase() === canonical.toLowerCase())) {
    options.unshift(canonical);
  }
  return options;
}
